import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GameService } from '../../../../features/private/services/game.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Game } from '../../../../shared/model/game';
import { ApiResponse } from '../../../../shared/model/api-response';
import { AlertService } from '../../../../shared/services/alert.service';
import { GenericFormComponent } from '../../../../shared/components/generic-form/generic-form.component';
import { Messages } from '../../../../shared/constants/messages';
import { NavigationHistoryService } from '../../../../core/services/navigation-history.service';
import { PermissionService } from '../../../../core/services/permission.service';
import { SystemPower } from '../../../../shared/model/permissions';
import { BggSearchResult } from '../../../../shared/model/bgg';

@Component({
  selector: 'app-game-edit',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, GenericFormComponent],
  templateUrl: './game-edit.component.html',
  styleUrl: './game-edit.component.css',
})
export class GameEditComponent implements OnInit {
  /** Se valorizzato il componente lavora in modalità embedded (es. modale admin): niente route né navigazione. */
  @Input() embeddedId?: number;
  @Output() saved = new EventEmitter<Game>();
  @Output() cancelled = new EventEmitter<void>();

  editGameForm: FormGroup;
  selectedGame = signal<Game | null>(null);

  private fb: FormBuilder = inject(FormBuilder);
  private gameService: GameService = inject(GameService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private alertService: AlertService = inject(AlertService);
  private navigationHistory = inject(NavigationHistoryService);
  private permissionService = inject(PermissionService);

  protected isAdministrator = computed(() => this.permissionService.hasSystemPower(SystemPower.ADMINISTRATOR));
  protected canRelink = computed(() => this.isAdministrator() && !this.selectedGame()?.bggId);

  protected relinkProvider = signal<'bgg' | 'rpg'>('bgg');
  protected relinkQuery = signal('');
  protected isRelinkSearching = signal(false);
  protected isRelinking = signal(false);
  protected relinkSearchResults = signal<BggSearchResult[]>([]);

  constructor() {
    this.editGameForm = this.fb.group({
      gameName: ['', Validators.required],
      gameDescription: ['', Validators.required],
      gameType: ['RPG', Validators.required],
      minPlayers: [0, [Validators.min(1)]],
      maxPlayers: [0, [Validators.min(1)]],
      yearPublished: [null],
      playingTime: [null],
      minAge: [null],
      imageUrl: [''],
      thumbnailUrl: [''],
      averageRating: [{ value: null, disabled: true }],
      bggRank: [{ value: null, disabled: true }],
    });
  }

  ngOnInit(): void {
    if (this.embeddedId) {
      this.loadGame(this.embeddedId);
      return;
    }
    const gameId = Number(this.route.snapshot.paramMap.get('id'));
    if (gameId) {
      this.loadGame(gameId);
    }
  }

  loadGame(id: number) {
    this.gameService.getAllGames().subscribe((response: ApiResponse<Game[]>) => {
      if (response.success && response.data) {
        const game = response.data.find((g: Game) => g.id === id);
        if (game) {
          this.selectedGame.set(game);
          this.editGameForm.patchValue({
            gameName: game.gameName,
            gameDescription: game.gameDescription,
            gameType: game.gameType,
            minPlayers: game.minPlayers,
            maxPlayers: game.maxPlayers,
            yearPublished: game.yearPublished ?? null,
            playingTime: game.playingTime ?? null,
            minAge: game.minAge ?? null,
            imageUrl: game.imageUrl ?? '',
            thumbnailUrl: game.thumbnailUrl ?? '',
            averageRating: game.averageRating ?? null,
            bggRank: null,
          });
        }
      }
    });
  }

  onSubmit() {
    if (this.editGameForm.valid && this.selectedGame()) {
      const formValue = this.editGameForm.getRawValue();
      const updatedGame: Partial<Game> = {
        id: this.selectedGame()!.id,
        bggId: this.selectedGame()!.bggId,
        gameName: formValue.gameName,
        gameDescription: formValue.gameDescription,
        gameType: formValue.gameType,
        minPlayers: Number(formValue.minPlayers),
        maxPlayers: Number(formValue.maxPlayers),
        yearPublished: formValue.yearPublished ? Number(formValue.yearPublished) : undefined,
        playingTime: formValue.playingTime ? Number(formValue.playingTime) : undefined,
        minAge: formValue.minAge ? Number(formValue.minAge) : undefined,
        imageUrl: formValue.imageUrl || undefined,
        thumbnailUrl: formValue.thumbnailUrl || undefined,
        averageRating: formValue.averageRating ? Number(formValue.averageRating) : undefined,
        approved: this.selectedGame()!.approved,
        creatorId: this.selectedGame()!.creatorId,
        creatorName: this.selectedGame()!.creatorName,
        tablesAssociated: this.selectedGame()!.tablesAssociated,
      };

      this.gameService.updateGame(updatedGame as Game).subscribe({
        next: (response: ApiResponse<Game>) => {
          if (response.success) {
            this.alertService.showSuccess(Messages.GAME_UPDATED_SUCCESS);
            if (this.embeddedId) {
              this.saved.emit(response.data ?? (updatedGame as Game));
              return;
            }
            this.router.navigate(['/games']);
          }
        },
      });
    }
  }

  protected searchRelink(): void {
    const query = this.relinkQuery().trim();
    const provider = this.relinkProvider();
    if (!query) {
      this.alertService.showError(`Inserisci un nome o ID per cercare su ${provider === 'rpg' ? 'RPGGeek' : 'BGG'}`);
      return;
    }

    this.isRelinkSearching.set(true);
    this.relinkSearchResults.set([]);

    if (/^\d+$/.test(query)) {
      const id = Number(query);
      this.gameService.getBggGameDetails(id, provider).subscribe({
        next: (res) => {
          this.isRelinkSearching.set(false);
          if (res.data) {
            this.relinkSearchResults.set([{
              id: res.data.id ?? id,
              type: res.data.type ?? (provider === 'rpg' ? 'rpgitem' : 'boardgame'),
              name: res.data.names?.[0] ?? { value: 'Sconosciuto' },
              yearPublished: res.data.yearPublished,
              source: provider
            }]);
          } else {
            this.alertService.showError('Nessun risultato trovato con questo ID');
          }
        },
        error: () => {
          this.isRelinkSearching.set(false);
          this.alertService.showError('Errore durante la ricerca per ID');
        }
      });
      return;
    }

    this.gameService.searchBggGames(query, provider).subscribe({
      next: (res) => {
        this.isRelinkSearching.set(false);
        const results = (res.data ?? []).map(r => ({ ...r, source: provider }));
        this.relinkSearchResults.set(results);
        if (results.length === 0) {
          this.alertService.showError('Nessun risultato trovato');
        }
      },
      error: () => {
        this.isRelinkSearching.set(false);
        this.alertService.showError('Errore durante la ricerca');
      }
    });
  }

  protected applyRelink(item: BggSearchResult): void {
    const currentGame = this.selectedGame();
    if (!currentGame || !currentGame.id) {
      return;
    }

    const provider = item.source ?? this.relinkProvider();
    this.isRelinking.set(true);

    this.gameService.getBggGameDetails(item.id, provider).subscribe({
      next: (res) => {
        const bggData = res.data;
        if (!bggData) {
          this.isRelinking.set(false);
          this.alertService.showError('Impossibile recuperare i dettagli per il ricongiungimento');
          return;
        }

        const updatedGame: Game = {
          ...currentGame,
          bggId: bggData.id ?? item.id,
          gameName: bggData.names?.[0]?.value || currentGame.gameName,
          gameDescription: bggData.description || currentGame.gameDescription,
          minPlayers: bggData.minPlayers?.value ? Number(bggData.minPlayers.value) : currentGame.minPlayers,
          maxPlayers: bggData.maxPlayers?.value ? Number(bggData.maxPlayers.value) : currentGame.maxPlayers,
          yearPublished: bggData.yearPublished?.value ? Number(bggData.yearPublished.value) : currentGame.yearPublished,
          playingTime: bggData.playingTime?.value ? Number(bggData.playingTime.value) : currentGame.playingTime,
          minAge: bggData.minAge?.value ? Number(bggData.minAge.value) : currentGame.minAge,
          imageUrl: bggData.imageUrl || currentGame.imageUrl,
          thumbnailUrl: bggData.thumbnailUrl || currentGame.thumbnailUrl,
          averageRating: bggData.statistics?.ratings?.average?.value ? Number(bggData.statistics.ratings.average.value) : currentGame.averageRating,
        };

        this.gameService.updateGame(updatedGame).subscribe({
          next: (updateRes) => {
            this.isRelinking.set(false);
            if (updateRes.success) {
              const resultGame = updateRes.data ?? updatedGame;
              this.alertService.showSuccess('Gioco riconfigurato e aggiornato con successo con i dati BGG/RPGGeek!');
              this.selectedGame.set(resultGame);
              this.editGameForm.patchValue({
                gameName: resultGame.gameName,
                gameDescription: resultGame.gameDescription,
                gameType: resultGame.gameType,
                minPlayers: resultGame.minPlayers,
                maxPlayers: resultGame.maxPlayers,
                yearPublished: resultGame.yearPublished ?? null,
                playingTime: resultGame.playingTime ?? null,
                minAge: resultGame.minAge ?? null,
                imageUrl: resultGame.imageUrl ?? '',
                thumbnailUrl: resultGame.thumbnailUrl ?? '',
                averageRating: resultGame.averageRating ?? null,
                bggRank: null,
              });
              this.relinkSearchResults.set([]);
              if (this.embeddedId) {
                this.saved.emit(resultGame);
              }
            }
          },
          error: () => {
            this.isRelinking.set(false);
            this.alertService.showError('Errore durante l\'aggiornamento del gioco ricongiunto');
          }
        });
      },
      error: () => {
        this.isRelinking.set(false);
        this.alertService.showError('Errore durante il recupero dei dati da BGG/RPGGeek');
      }
    });
  }

  goBack() {
    if (this.embeddedId) {
      this.cancelled.emit();
      return;
    }
    this.navigationHistory.goBack(['/games']);
  }
}
